import moment from 'moment';
import { getTaskCalendar } from '../../../../assets/js/events/utils/getTaskCalendar';
import { GANTT_STYLE_MODE } from '../../constants';

const PROGRESS_PERCENTAGE_DIVISOR = 100;
const PROGRESS_ZERO = 0;
const PROGRESS_COMPLETE = 100;
const ZOOM_LEVEL_DAYS = 'DAYS';

/**
 * Calculates progress percentage from task progress value
 * @param {Object} task - Task object containing progress property
 * @param {number} task.progress - Progress value (0-100)
 * @returns {number} Progress percentage as decimal (0-1)
 * @example
 * const task = { progress: 75 };
 * const percentage = calculateProgressPercentage(task); // Returns 0.75
 */
const calculateProgressPercentage = (task) => {
  return task?.progress / PROGRESS_PERCENTAGE_DIVISOR;
};

/**
 * Calculates the total number of calendar days for a task
 * @param {Object} task - Task object containing start and end dates
 * @param {string|Date} task.start_date - Task start date
 * @param {string|Date} task.end_date - Task end date
 * @returns {number} Total number of calendar days (inclusive)
 * @example
 * const task = { start_date: '2024-01-01', end_date: '2024-01-05' };
 * const days = getDayCalendar(task); // Returns 5
 */
const getDayCalendar = (task) => {
  const start = moment(task.start_date);
  const end = moment(task.end_date);
  const days = end.diff(start, 'days') + 1;

  return days;
};

/**
 * Generates an array of dates between task start and end dates
 * @param {Object} task - Task object containing start and end dates
 * @param {string|Date} task.start_date - Task start date
 * @param {string|Date} task.end_date - Task end date
 * @returns {Date[]} Array of dates from start to end (inclusive)
 * @example
 * const task = { start_date: '2024-01-01', end_date: '2024-01-03' };
 * const dates = getDateRange(task); // Returns [Date, Date, Date]
 */
const getDateRange = (task) => {
  const start = moment(task.start_date);
  const end = moment(task.end_date);
  const days = end.diff(start, 'days') + 1;

  return Array.from({ length: days }, (_, i) =>
    start.clone().add(i, 'days').toDate()
  );
};

const getDurationInDays = (range, calendar) => {
  const dateWorkTime = range.filter((date) => {
    const isDateWorkTime = calendar.isWorkTime({
      date: date,
      unit: 'day'
    });

    return isDateWorkTime;
  });

  return dateWorkTime.length;
};

/**
 * Calculates progress percentage based on working days for BLOCKS style gantt
 * @param {Object} gantt - Gantt instance with calendar configuration
 * @param {Object} task - Task object containing progress and duration
 * @param {number} task.progress - Progress value (0-100)
 * @param {number} task.duration - Task duration in hours
 * @param {string|Date} task.start_date - Task start date
 * @param {string|Date} task.end_date - Task end date
 * @returns {number} Progress percentage as decimal (0-1) based on working days
 * @example
 * const gantt = { /* gantt instance with calendar *\/ };
 * const task = { progress: 50, duration: 40, start_date: '2024-01-01', end_date: '2024-01-10' };
 * const progress = getWorkingDaysProgress(gantt, task);
 */
const getWorkingDaysProgress = (gantt, task) => {
  const { duration, progress } = task;

  if (progress === PROGRESS_ZERO || progress === PROGRESS_COMPLETE) {
    return calculateProgressPercentage(task);
  }

  const calendar = getTaskCalendar(task, gantt);

  const dayCalendar = getDayCalendar(task);
  const dateRange = getDateRange(task);
  const durationInDays = getDurationInDays(dateRange, calendar);
  const dayCalendarProgress =
    (durationInDays * progress) / PROGRESS_PERCENTAGE_DIVISOR;

  let calendarDaysNeeded = 0;
  let workingDaysCounted = 0;

  for (const date of dateRange) {
    const isDateWorkTime = calendar.isWorkTime({
      date: date,
      unit: 'day'
    });

    calendarDaysNeeded++;

    if (isDateWorkTime) {
      workingDaysCounted++;

      if (workingDaysCounted >= dayCalendarProgress) {
        if (workingDaysCounted > dayCalendarProgress) {
          const excess = workingDaysCounted - dayCalendarProgress;
          calendarDaysNeeded -= excess;
        }
        break;
      }
    }
  }

  const progressPercentage = calendarDaysNeeded / dayCalendar;
  return progressPercentage;
};

/**
 * Calculates progress percentage based on gantt configuration and task
 * @param {Object} gantt - Gantt instance with configuration
 * @param {string} gantt.barFormat - Gantt barFormat (BARS or BLOCKS)
 * @param {Object} gantt.config - Gantt configuration object
 * @param {boolean} gantt.config.drag_progress - Whether drag progress is in progress
 * @param {Object} task - Task object
 * @param {number} task.progress - Progress value (0-100)
 * @param {boolean} task.is_parent - Whether task is a parent task
 * @param {number} task.duration - Task duration in hours
 * @param {string|Date} task.start_date - Task start date
 * @param {string|Date} task.end_date - Task end date
 * @returns {number} Progress percentage as decimal (0-1)
 * @example
 * const gantt = { barFormat: 'BLOCKS', config: { drag_progress: false } };
 * const task = { progress: 60, is_parent: false, duration: 40, start_date: '2024-01-01', end_date: '2024-01-10' };
 * const progress = calculateProgressWithGantt(gantt, task);
 */
export const calculateProgressWithGantt = (gantt, task) => {
  if (!gantt) {
    return calculateProgressPercentage(task);
  }

  if (gantt.barFormat === GANTT_STYLE_MODE.BARS) {
    return calculateProgressPercentage(task);
  }

  const dragInProgress = gantt.config?.drag_progress;

  if (dragInProgress) {
    return calculateProgressPercentage(task);
  }

  if (gantt.barFormat === GANTT_STYLE_MODE.BLOCKS) {
    if (task.is_parent) {
      return calculateProgressPercentage(task);
    }

    return getWorkingDaysProgress(gantt, task);
  }

  return calculateProgressPercentage(task);
};

/**
 * Gets the progress percentage for a task, considering gantt visualization context
 * @param {Object} task - Task object
 * @param {number} task.progress - Progress value (0-100)
 * @param {boolean} task.is_parent - Whether task is a parent task
 * @param {number} task.duration - Task duration in hours
 * @param {string|Date} task.start_date - Task start date
 * @param {string|Date} task.end_date - Task end date
 * @returns {number} Progress percentage as decimal (0-1)
 * @example
 * const task = { progress: 75, is_parent: false, duration: 40, start_date: '2024-01-01', end_date: '2024-01-10' };
 * const progress = getProgressPercentage(task);
 */
export const getProgressPercentage = (task) => {
  try {
    const lookahead = window?.ganttVisualization;

    if (!lookahead) {
      return calculateProgressPercentage(task);
    }

    const currentLevel = lookahead.stringCurrentValue;

    if (currentLevel !== ZOOM_LEVEL_DAYS) {
      return calculateProgressPercentage(task);
    }

    return calculateProgressWithGantt(lookahead, task);
  } catch (error) {
    return PROGRESS_ZERO;
  }
};
